import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Star, Crown, User, Eye, VerifiedIcon } from "lucide-react";
import { getRequest, postRequest } from "../../helpers/api.helper";
import { UPLOAD_URL } from "../../utils/constants";
import { getGenderPlaceholder } from "../../helpers/matches.helper";
import { ProfileType } from "../../types";
import ProfileSection from "../ProfileDetails/Components/ProfileSection";
import GroupedProfileSection from "../ProfileDetails/Components/GroupedProfileSection";
import ProfileImage from "../ProfileDetails/Components/ProfileImage";
import ProfileGallery from "../ProfileDetails/Components/ProfileGallery";
import { VoicePlayer } from "../../components/VoicePlayer";
import { useLanguage } from "../../context/LanguageContext";
import { resolveVoiceMessageUrl } from "../../helpers/voiceMessage.helper";
import {
  formatCreatedBy,
  isCreatedByOtherMediator,
} from "../../utils/createdBy";

const buildAboutText = (profile: Record<string, any>) => {
  const summary = Array.isArray(profile.customWords)
    ? profile.customWords
        .map((entry: unknown) => String(entry ?? "").trim())
        .filter(Boolean)
    : [];
  const otherBusiness =
    profile.otherBusiness && profile.otherBusiness !== "null"
      ? String(profile.otherBusiness).trim()
      : "";

  const chunks: string[] = [];
  if (summary.length) chunks.push(summary.join(" "));
  if (otherBusiness) chunks.push(`Other business: ${otherBusiness}.`);
  return chunks.length
    ? chunks.join(" ")
    : "No additional information provided.";
};

const buildTalents = (profile: Record<string, any>) => {
  const skills = Array.isArray(profile.extraTalentedSkills)
    ? profile.extraTalentedSkills
        .map((entry: unknown) => String(entry ?? "").trim())
        .filter(Boolean)
    : [];

  return skills.join(", ");
};

const WebsiteProfileDetail = () => {
  const { profileId, id } = useParams<{ profileId: string; id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    if (!profileId) {
      setError("Profile not found.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await getRequest(
        `/bureau/website/${id}/profile/${encodeURIComponent(profileId)}`
      );
      if (!response.success || !response.data) {
        setError(response.message ?? "Unable to load profile details.");
        return;
      }
      setProfile(response.data);
    } catch (err: any) {
      console.error("Failed to fetch profile detail:", err);
      setError("Unable to load profile details right now.");
    } finally {
      setLoading(false);
    }
  };

  const hasIncrementedView = React.useRef(false);
  useEffect(() => {
    hasIncrementedView.current = false; // ✅ reset when profile changes
  }, [profileId]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    fetchProfile();

    if (!hasIncrementedView.current && profileId) {
      hasIncrementedView.current = true;
      postRequest(`/common/profile/${profileId}/view`, {}).catch(() => {});
    }
  }, [profileId]);

  useEffect(() => {
    if (!loading) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      });
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 ">
        <div className="flex flex-col items-center space-y-4 text-purple-600">
          <svg
            className="animate-spin h-8 w-8"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <p className="text-sm font-semibold">Loading profile…</p>
        </div>
      </div>
    );
  }

  const formatDate = (isoString: string): string => {
    if (!isoString) return "-";

    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    // Show relative time only if within 30 days
    if (diffDay < 30) {
      if (diffSec < 60) return "just now";
      if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
      if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
      return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
    }

    // Otherwise show formatted date
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-md p-6 text-center space-y-4">
          <h2 className="text-lg font-semibold text-red-600">
            {error ?? "Profile not available."}
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center px-5 py-2 rounded-lg bg-purple-600 text-white font-semibold shadow hover:bg-purple-700 transition"
          >
            {t("Go Back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="min-h-screen bg-gray-50 pt-4 pb-8">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 flex flex-col lg:flex-row gap-2">
          {/* Back Button */}
          <div>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium text-sm px-3 py-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition"
            >
              ← {t("Back")}
            </button>
          </div>

          <div className="flex-1 space-y-4">
            {/* PROFILE CARD */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="flex flex-col sm:flex-row">
                {/* Profile Image */}
                <div className="md:w-80 sm:flex-shrink-0">
                  <ProfileImage
                    profile={profile}
                    UPLOAD_URL={UPLOAD_URL}
                    getGenderPlaceholder={getGenderPlaceholder}
                  />
                </div>

                {/* Profile Content */}
                <div className="flex flex-col md:flex-row gap-4 sm:gap-6 p-4 sm:p-6 px-5 flex-1">
                  <div className="flex-1 space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-4 pt-2">
                      <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                        {t("Score")}: {profile.completeness}%
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-green-600">
                          <Eye size={16} />
                          <span className="text-sm font-semibold">
                            {profile.views} {t("Views")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 truncate">
                          {profile.fullName}
                        </h1>
                        <div className="flex items-center gap-2">
                          <p className="text-xs flex gap-1 sm:text-sm font-semibold text-gray-800 bg-gray-100 rounded-full px-2 py-1">
                            {t("ID")}: {profile.martialId}
                            {profile.completeness >= 100 && (
                              <VerifiedIcon size={16} color="green" />
                            )}
                          </p>
                          {profile.citizenship && (
                            <p className="text-xs flex sm:text-sm font-bold text-gray-800 bg-gray-100 rounded-full px-2 py-1">
                              {profile.citizenship}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-xs flex text-gray-800 bg-gray-100 rounded-full px-2 py-1">
                        {t("Last seen")}:{" "}
                        <span className="font-semibold">
                          {formatDate(profile.updatedAt)}
                        </span>
                      </p>
                      <p className="text-xs flex text-gray-800 bg-gray-100 rounded-full px-2 py-1">
                        {t("Created")}:{" "}
                        <span className="font-semibold">
                          {formatDate(profile.createdAt)}
                        </span>
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-1 sm:gap-2 text-xs sm:text-sm">
                      <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                        {profile.maritalStatus}
                      </span>
                      <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                        {t("Profile created by")} {formatCreatedBy(profile?.createdBy)}
                      </span>
                      <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                        {profile.age}Yrs
                      </span>
                      <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                        {profile.height}Ft
                      </span>
                      {profile.caste && (
                        <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                          {profile.caste}
                        </span>
                      )}
                      {profile.education && (
                        <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                          {profile.education}
                        </span>
                      )}
                      {profile.occupation && (
                        <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                          {profile.occupation}, {profile.jobLocation}
                        </span>
                      )}
                      {profile.annualIncome && (
                        <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                          {profile.annualIncome}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1 sm:gap-2 text-xs sm:text-sm">
                      {profile.district && (
                        <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                          {profile.district}
                        </span>
                      )}
                      {profile.state && (
                        <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                          {profile.state}
                        </span>
                      )}
                      {profile.country && (
                        <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                          {profile.country}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {profile.voiceMessage && (
              <section className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <User className="text-pink-600" size={18} /> Voice Message
                </h2>
                <VoicePlayer src={resolveVoiceMessageUrl(profile.voiceMessage)} />
              </section>
            )}

            {/* PERSONAL INFO */}
            <ProfileSection
              title="Personal Information"
              icon={User}
              data={[
                ["Date of Birth", formatDate(profile.dateOfBirth)],
                ["Age", profile.age],
                ["Birth Place", profile.birthPlace],
                ["Birth Time", profile.birthTime],
                ["Height", profile.height],
                ["Weight", profile.weight],
                ["Body Type", profile.bodyType],
                ["Languages Known", profile.spokenLanguages],
                ["Profile Created By", profile.createdBy],
                ["Marital Status", profile.maritalStatus],
                ["Lives In", profile.location],
              ]}
            />

            {/* HABITS */}
            <section className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                <Star className="text-pink-600" size={18} /> Habits:
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-600">Eating Habits</span>
                  <span className="font-medium text-right">
                    {profile.eatingHabits}
                  </span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-600">Smoking Habits:</span>
                  <span className="font-medium text-right">
                    {profile.smokingHabits}
                  </span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-600">Drinking Habits:</span>
                  <span className="font-medium text-right">
                    {profile.drinkingHabits}
                  </span>
                </div>
              </div>
            </section>

            {/* RELIGION & CASTE DETAILS */}

            <ProfileSection
              title="Religion & Caste Details"
              icon={User}
              data={[
                ["Religion", profile.religion],
                ["Mother Tongue", profile.motherTongue],
                ["Caste", profile.caste],
                ["Sub-Caste", profile.subcaste],
                ["Gothra", profile.gotram],
                ["Uncle's Gothram", profile.gotram],
                ["Dosham", profile.dosham],
              ]}
            />

            <ProfileSection
              title="Horoscope Information"
              icon={User}
              data={[
                ["Star", profile.star],
                ["Rashi", profile.raasi],
                ["Dosham", profile.dosham],
                ["Suitable star", profile.suitableStar],
              ]}
            />

            <ProfileSection
              title="Profations details"
              icon={User}
              data={[
                ["Education", profile.education],
                ["Employment", profile.employmentStatus],
                ["Occupation", profile.occupation],
                ["Income", profile.annualIncome],
                ["Job Location", profile.jobLocation],
              ]}
            />

            {/* FAMILY */}
            <ProfileSection
              title="Family Details"
              icon={User}
              data={[
                ["Father Employee", profile.fatherEmployee],
                ["Father Occupation", profile.fatherOccupied],
                ["Mother Employee", profile.motherEmployee],
                ["Mother Occupation", profile.motherOccupied],
                ["Family Type", profile.familyType],
                ["Family Value", profile.familyValue],
                ["Total Brothers", profile.totalBrothers],
                ["Elder Brothers", profile.elderBrothers],
                ["Younger Brothers", profile.youngerBrothers],
                ["Total Sisters", profile.totalSisters],
                ["Elder Sisters", profile.elderSisters],
                ["Younger Sisters", profile.youngerSisters],
                ["Native Place", profile.originalLocation],
              ]}
            />

            {/* PROPERTY DETAILS */}

            <ProfileSection
              title="Property details"
              icon={User}
              data={[
                ["House Type", profile.houseType],
                ["No of Houses", profile.numberOfHouses],
                ["Own House sq Feet", profile.houseSqFeet],
                ["Own House Value", profile.houseValue],
                ["Own House Location", profile.houseLocation],
                ["Commercial shops", profile.commercialshops],
                ["Shop Sq Yards", profile.shopssqyards],
                ["Monthly Rent", profile.monthlyRent],
                ["Open Plots", profile.openPlots],
                ["Open Plots Sq Yards", profile.openPlotsSqFeet],
                ["Open Plots Total Value", profile.openPlotsValue],
                ["Plot Location", profile.openPlotsLocation],
                ["Agricultural Land", profile.agricultureLand],
                ["Agricultural Land Value", profile.agricultureLandValue],
                ["Agriculture Land Location", profile.agricultureLandLocation],
                ["Flat Type", profile.flatType],
                ["Flat Value", profile.flatValue],
                ["Flat Location", profile.flatLocation],
                ["Total Property Value", profile.totalPropertiesValue],
                ["Any More Properties", profile.anyMoreProperties],
              ]}
            />

            <ProfileGallery profile={profile} />

            {/* ABOUT */}
            {(profile.customWords ||
              profile.extraTalentedSkills ||
              !!profile.otherBusiness ||
              !!profile.otherBusinessIncome) && (
              <section className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <User className="text-pink-600" size={18} /> About Myself
                </h2>
                {buildAboutText(profile)}
              </section>
            )}

            {profile.extraTalentedSkills &&
              profile.extraTalentedSkills.length > 0 && (
                <section className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                    <User className="text-pink-600" size={18} /> Extra Talented
                    Skills
                  </h2>
                  {buildTalents(profile)}
                </section>
              )}

            <GroupedProfileSection
              title={t("partner_preference")}
              icon={User}
              data={[
                {
                  title: t("preferences.basic"),
                  items: [
                    [
                      t("preferences.preferredAge"),
                      profile.agePreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredHeight"),
                      profile.heightPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredMaritalStatus"),
                      profile.maritalStatusPreferences?.join(", "),
                    ],
                    [
                      t("preferences.physicalStatus"),
                      profile.physicalStatusPreference,
                    ],
                    [
                      t("preferences.eatingHabits"),
                      profile.eatingHabitsPreference,
                    ],
                    [
                      t("preferences.drinkingHabits"),
                      profile.drinkingHabitsPreference,
                    ],
                    [
                      t("preferences.smokingHabits"),
                      profile.smokingHabitsPreference,
                    ],
                  ],
                },
                {
                  title: t("preferences.religious"),
                  items: [
                    [
                      t("preferences.preferredReligion"),
                      profile.religionPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredMotherTongue"),
                      profile.motherTonguePreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredCaste"),
                      profile.castePreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredSubcaste"),
                      profile.subCastePreferences?.join(", "),
                    ],
                  ],
                },
                {
                  title: t("preferences.property"),
                  items: [
                    [
                      t("preferences.preferredHouseType"),
                      profile.ownHousePreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredHouseSqYards"),
                      profile.squareYardPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredMonthlyRent"),
                      profile.monthlyRentPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredOpenPlot"),
                      profile.plotPreference?.join(", "),
                    ],
                    [
                      t("preferences.preferredFlat"),
                      profile.flatPreference?.join(", "),
                    ],
                    [
                      t("preferences.preferredOwnLocation"),
                      profile.ownLocationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredAgriculturalLand"),
                      profile.agricultureLandPreference?.join(", "),
                    ],
                    [
                      t("preferences.totalPropertyValue"),
                      profile.totalPropertyValuePreference?.join(", "),
                    ],
                  ],
                },
                {
                  title: t("preferences.professional"),
                  items: [
                    [
                      t("preferences.preferredEducation"),
                      profile.partnerEducationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredEmployment"),
                      profile.partnerOccupationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredOccupation"),
                      profile.partnerOccupationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredAnnualIncome"),
                      profile.annualIncomePreference?.join(" ,"),
                    ],
                    [
                      t("preferences.preferredFamily"),
                      profile.familyPreferences?.join(" ,"),
                    ],
                    [
                      t("preferences.preferredSettledLocation"),
                      profile.settledLocationPreferences?.join(" ,"),
                    ],
                  ],
                },
                {
                  title: t("preferences.location"),
                  items: [
                    [
                      t("preferences.preferredCountry"),
                      profile.countryLocationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredState"),
                      profile.stateLocationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredCity"),
                      profile.cityLocationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredCitizenship"),
                      profile.citizenshipPreferences?.join(", "),
                    ],
                  ],
                },
                {
                  title: t("preferences.horoscope"),
                  items: [
                    [
                      t("preferences.preferredStar"),
                      profile.starPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredRashi"),
                      profile.rashiPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredDosham"),
                      profile.doshamPreferences?.join(", "),
                    ],
                  ],
                },
                {
                  title: t("preferences.habits"),
                  items: [
                    [
                      t("eating_habits"),
                      profile.eatingHabitsPreferences?.join(", "),
                    ],
                    [
                      t("drinking_habits"),
                      profile.drinkingHabitsPreferences?.join(", "),
                    ],
                    [
                      t("smoking_habits"),
                      profile.smokingHabitsPreferences?.join(", "),
                    ],
                  ],
                },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteProfileDetail;
